import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import RobustSelect from '../../components/RobustSelect';
import { SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Users as UsersIcon, UserPlus, Upload, Download, Trash2, Edit, Search } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import BulkUploadProgress from '../../components/BulkUploadProgress';

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    batch_id: 'none',
  });
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUsers();
    fetchBatches();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [roleFilter, batchFilter, searchQuery, users]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      batch_id: user.batch_id || 'none',
      plain_password: '',
    });
    setEditModalOpen(true);
    // Fetch user details to get plain_password
    try {
      const res = await api.get(`/users/${user.id}/details`);
      if (res.data.plain_password) {
        setEditFormData(prev => ({ ...prev, plain_password: res.data.plain_password }));
      }
    } catch (e) {
      // ignore — password may not be stored
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Prepare update data - convert "none" to null
      const updateData = {
        name: editFormData.name,
        email: editFormData.email,
      };
      
      // Only include batch_id if it's not "none"
      if (editFormData.batch_id !== 'none') {
        updateData.batch_id = editFormData.batch_id;
      } else {
        updateData.batch_id = null;
      }

      // Include new password if admin set one
      if (editFormData.new_password && editFormData.new_password.trim()) {
        updateData.new_password = editFormData.new_password.trim();
      }
      
      await api.put(`/users/${editingUser.id}`, updateData);
      toast.success('User updated successfully');
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const filterUsers = () => {
    let filtered = users;
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    if (batchFilter !== 'all') {
      if (batchFilter === 'none') {
        filtered = filtered.filter(u => !u.batch_id);
      } else {
        filtered = filtered.filter(u => u.batch_id === batchFilter);
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(u =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    setFilteredUsers(filtered);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all non-super_admin users
      const selectableUsers = filteredUsers.filter(u => u.role !== 'super_admin').map(u => u.id);
      setSelectedUsers(new Set(selectableUsers));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) {
      toast.error('No users selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.size} user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setBulkDeleting(true);
    try {
      const response = await api.delete('/users/bulk', { 
        data: { user_ids: Array.from(selectedUsers) }
      });
      toast.success(response.data.message);
      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setBulkDeleting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const testsResponse = await api.get('/tests');
      const tests = testsResponse.data;
      const batchesResponse = await api.get('/batches');
      const batches = batchesResponse.data;
      
      let template = 'name,email,password,role,test_ids,batch_id\n';
      template += 'John Doe,john@example.com,password123,user,,\n';
      template += 'Jane Smith,jane@example.com,password456,admin,,\n';
      template += '\n# Available Test IDs:\n';
      tests.forEach(test => {
        template += `# ${test.id} - ${test.title}\n`;
      });
      template += '\n# Available Batch IDs:\n';
      batches.forEach(batch => {
        template += `# ${batch.id} - ${batch.name}\n`;
      });
      template += '\n# Note: Separate multiple test_ids with semicolon (;)\n';
      template += '# Example: abc123;def456';

      const blob = new Blob([template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_template.csv';
      a.click();
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Failed to generate template');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/users/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Successfully uploaded ${response.data.users_created} users`);
      setUploadOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Users</h1>
            <p className="text-gray-600">Manage platform users</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              data-testid="download-template-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <Button
                variant="outline"
                onClick={() => setUploadOpen(true)}
                data-testid="bulk-upload-users-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Users</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 text-sm mb-2">How to get Test IDs:</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Download the template - it includes available Test IDs</li>
                      <li>Or go to Tests page and click the copy button next to any test</li>
                      <li>Multiple Test IDs should be separated by semicolon (;)</li>
                      <li>Example: <code className="bg-white px-2 py-0.5 rounded">abc123;def456;ghi789</code></li>
                    </ol>
                  </div>
                  <BulkUploadProgress
                    endpoint="/users/bulk-upload-stream"
                    title="Bulk Upload Users"
                    description="Upload a CSV or Excel file with users. Required columns: name, email, password, role (user/admin), test_ids (semicolon-separated, optional)"
                    onComplete={() => { fetchUsers(); toast.success('Users uploaded successfully'); }}
                    onClose={() => setUploadOpen(false)}
                  />
                </div>
              </DialogContent>
            </Dialog>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/admin/users/create')}
              data-testid="create-user-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
        </div>

        <Card className="p-4 mb-4">
          <div className="space-y-3">
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
              <Label className="font-medium">Filter by Role:</Label>
              <RobustSelect
                value={roleFilter}
                onValueChange={setRoleFilter}
                placeholder="Select role"
                className="w-48"
                testId="role-filter-select"
              >
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="user">Test Users</SelectItem>
                <SelectItem value="admin">Administrators</SelectItem>
                <SelectItem value="faculty">Faculty</SelectItem>
                {currentUser.role === 'super_admin' && (
                  <SelectItem value="super_admin">Super Admins</SelectItem>
                )}
              </RobustSelect>
              <Label className="font-medium">Batch:</Label>
              <RobustSelect
                value={batchFilter}
                onValueChange={setBatchFilter}
                placeholder="Select batch"
                className="w-56"
                testId="batch-filter-select"
              >
                <SelectItem value="all">All Batches</SelectItem>
                <SelectItem value="none">No Batch</SelectItem>
                {batches.map((batch) => (
                  <SelectItem key={batch.id} value={batch.id}>
                    {batch.name} {batch.year && `(${batch.year})`}
                  </SelectItem>
                ))}
              </RobustSelect>
              <span className="text-sm text-gray-600">
                Showing {filteredUsers.length} of {users.length} users
              </span>
            </div>
            {selectedUsers.size > 0 && currentUser.role === 'super_admin' && (
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-users-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {bulkDeleting ? 'Deleting...' : `Delete ${selectedUsers.size} Selected`}
              </Button>
            )}
          </div>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {currentUser.role === 'super_admin' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                      <Checkbox
                        checked={selectedUsers.size > 0 && selectedUsers.size === filteredUsers.filter(u => u.role !== 'super_admin').length}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-users"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={currentUser.role === 'super_admin' ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                      <UsersIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const userBatch = batches.find(b => b.id === user.batch_id);
                    return (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        {currentUser.role === 'super_admin' && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            {user.role !== 'super_admin' && (
                              <Checkbox
                                checked={selectedUsers.has(user.id)}
                                onCheckedChange={(checked) => handleSelectUser(user.id, checked)}
                                data-testid={`select-user-${user.id}`}
                              />
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={user.role === 'admin' || user.role === 'super_admin' || user.role === 'faculty' ? 'default' : 'secondary'}>
                            {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : user.role === 'faculty' ? 'Faculty' : 'User'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {userBatch ? `${userBatch.name} (${userBatch.year})` : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={user.status === 'active' ? 'success' : 'secondary'}>
                            {user.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {user.role !== 'super_admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(user.id)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Edit User Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">Current Password</Label>
                <Input
                  id="edit-password"
                  value={editFormData.plain_password || ''}
                  readOnly
                  className="bg-gray-50 font-mono"
                  placeholder="Not available (hashed)"
                  data-testid="edit-user-password"
                />
                {!editFormData.plain_password && (
                  <p className="text-xs text-amber-600">Password was set before storage — cannot be recovered.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-new-password">Set New Password</Label>
                <Input
                  id="edit-new-password"
                  type="text"
                  value={editFormData.new_password || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, new_password: e.target.value })}
                  placeholder="Leave empty to keep current password"
                  className="font-mono"
                  data-testid="edit-user-new-password"
                />
                <p className="text-xs text-gray-500">Enter a new password to reset. Leave blank to keep unchanged.</p>
              </div>
              {editingUser?.role === 'user' && (
                <div className="space-y-2">
                  <Label htmlFor="edit-batch">Batch</Label>
                  <RobustSelect
                    value={editFormData.batch_id}
                    onValueChange={(value) => setEditFormData({ ...editFormData, batch_id: value })}
                    placeholder="Select batch"
                  >
                    <SelectItem value="none">None</SelectItem>
                    {batches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.id}>
                        {batch.name} {batch.year && `(${batch.year})`}
                      </SelectItem>
                    ))}
                  </RobustSelect>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Changes</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Users;